import { DEFAULT_MAP_SOURCE } from '@env';
import Hls from 'hls.js';
import { ToastrService } from 'ngx-toastr';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  OnInit,
  Inject,
  Component,
  OnDestroy,
  HostListener,
  HostBinding,
  EventEmitter,
  Output,
  Input,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnAuditOperation, SnCamera, SnContentType } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';
import { PursuitService } from '@lib/cameras/services/pursuit.service';
import { ISnPlayer } from '@lib/player/common';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';

import { PtzControlsService } from '../../ptz-controls.service';
import { SnHlsPlayerReference } from '../../references';
import { VideoPlaybackService } from '../../video-playback.service';
import { SnVideoPlayerComponent } from '../../video-player.component';
import { MapStreetViewComponentComponent } from '../map-street-view-component/map-street-view-component.component';
import { VideoPlayerClipControl } from '../video-player-clip/video-player-clip.control';

@Component({
  selector: 'sn-video-player-analysis',
  templateUrl: './video-player-analysis.control.html',
  styleUrls: ['./video-player-analysis.control.scss'],
})
export class SnVideoPlayerAnalysisControl implements OnInit, OnDestroy {
  @Input() public camera: SnCamera;
  @Input() public alwaysOn = false;
  @Input() public ptzConfig;
  public showControls = false;
  public isDrawing: boolean = false;
  public isClipping: boolean = false;
  public showCalendar: boolean = false;
  public canClip: boolean;
  public isrewind = false;
  public rewindInterval;
  @Output() public readonly toggleCalendar = new EventEmitter<boolean>();
  @HostBinding('class.seeThrough')
  get isAreaZoomDrawing(): boolean {
    return this.isDrawing;
  }

  get paused(): boolean {
    return !this._player.isPlaying;
  }

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  get videoElement(): HTMLVideoElement {
    return this._player?.video;
  }

  public _currentTime: number;
  private dialogRef: MatDialogRef<VideoPlayerClipControl>;
  private _streetViewDialogRef: MatDialogRef<MapStreetViewComponentComponent>;
  private lastMediaTime;
  private lastFrameNum;
  private fps;
  private fpsRounder = [];
  private frameNotSeeked = true;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject('SN_PLAYER') private readonly _player: SnVideoPlayerComponent,
    private readonly _matDialog: MatDialog,
    private readonly _auditService: SnAuditService,
    private readonly _ptzService: PtzControlsService,
    private readonly _dialog: MatDialog,
    private readonly _playbackService: VideoPlaybackService,
    private readonly _toastrService: ToastrService,
    private readonly _ability: SnAbilityService,
    private readonly _pursuitService: PursuitService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _elementRef: ElementRef,
  ) {}

  get loopDuration(): number {
    return this._player.loopDuration * 60;
  }

  get player(): ISnPlayer {
    return this._player;
  }

  get isBuffering(): boolean {
    return (
      this._player?.reference instanceof SnHlsPlayerReference &&
      this._player?.reference?.isBufferStalled
    );
  }

  get playbackRate(): number {
    return this._player.reference.playbackRate;
  }
  set playbackRate(value: number) {
    if (this._player.reference.playbackRate) {
      this._player.reference.playbackRate = value;
    }
  }

  get currentTime(): any {
    return this._player.reference.currentTime;
  }
  set currentTime(value: any) {
    if (this._player.reference.currentTime) {
      this._player.reference.currentTime = value;
    }
  }

  get rewindSpeedFactor(): number {
    return this._player.reference?.rewindSpeedFactor;
  }

  get isFastForwarding(): boolean {
    return this._player.reference?.isFastForwarding;
  }

  get isRewinding(): boolean {
    return this._player.reference?.isRewinding;
  }

  public onSpace(): void {
    this._player.pause();
  }

  public ngOnInit(): void {
    this.canClip = this._ability.can({ subject: 'cameras/camera', action: 'clip_recordings' });
    this._pursuitService
      .getScreenshot()
      .pipe(takeUntil(this._destroy$))
      .subscribe((value: boolean) => {
        if (value) {
          this.snapAi();
        }
      });
    this._player.fullscreenChange.pipe(takeUntil(this._destroy$)).subscribe((value: boolean) => {
      if (!value && this._streetViewDialogRef) {
        this._streetViewDialogRef.close();
      }
    });
    this._player.currentTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      this._currentTime = time;
    });

    this._player.play$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      const video = this._player.elementRef.nativeElement.querySelector('video');
      if (video) {
        const agent = window.navigator.userAgent.toLowerCase();
        const isChrome = agent.indexOf('chrome') > -1 && !!(<any>window).chrome;
        if (isChrome) {
          video.requestVideoFrameCallback(this._getVideoFramerate.bind(this));
          video.addEventListener('seeked', () => {
            this.fpsRounder?.pop();
            this.frameNotSeeked = false;
          });
        }
      }
    });

    merge(this._player.reference?.destroyed$, this._player.init.asObservable())
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._player.reference.fragLoading = true;
      });

    this._player.fragLoading$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      if (this._player.reference.isRewinding) {
        this._player.reference.fragLoading = true;
      }
    });

    this._player.fragLoaded$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      if (this._player.reference.isRewinding) {
        this._player.reference.fragLoading = false;
      }
    });

    this._player.reference.rewindStop$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.pauseRewind();
    });

    this._ptzService.ptzMovement$
      .pipe(takeUntil(this._destroy$))
      .subscribe((isDrawing) => (this.isDrawing = isDrawing));

    this._playbackService.rewindPause$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.pauseRewind();
      this._cd.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('document:keydown.arrowleft')
  public onArrowLeft(): void {
    if (this.ptzConfig?.ptzControlEnableArrows) {
      return;
    }
    this.back(0.5);
  }

  public fastForward(): void {
    this._player.reference.fastForward();
  }

  public rewind(): void {
    this._player.reference.rewind();
    if (this._player.reference.rewindSpeedFactor > 0) {
      this._playbackService.rewindInit();
    }
  }

  public stopRewinding(): void {
    this._player.reference.stopRewinding();
  }

  public back(amount: number): void {
    if (this._player.fullscreen && this._currentTime) {
      this._player.pause();
      this._player.changeCurrentTime(this._currentTime - amount);
    }
  }

  public framesBack(): void {
    this.fps = this.fps ? this.fps : 30;
    const seconds = 1 / this.fps;
    this.back(seconds);
  }

  public framesForward(): void {
    this.fps = this.fps ? this.fps : 30;
    const seconds = 1 / this.fps;
    this.forward(seconds);
  }

  @HostListener('document:keydown.arrowright')
  public onArrowRight(): void {
    if (this.ptzConfig?.ptzControlEnableArrows) {
      return;
    }
    this.forward(0.5);
  }

  public forward(amount: number): void {
    if (this._player.fullscreen && this._currentTime) {
      this._player.pause();
      this._player.changeCurrentTime(this._currentTime + amount);
    }
  }

  public snapAi(): void {
    this._toastrService.overlayContainer = this._player.toastContainer;
    this._player.isClipping = true;
    const video = this._player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    canvas.toBlob((blob) => {
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `snapshot-${name}.jpeg`;
      const dataSnap = {
        file,
        camera: this.camera,
      };
      this._pursuitService.setSnapAi(dataSnap);
    });
  }

  public clip(): void {
    const payload: any = {
      object: {
        id: this.camera.id,
        name: this.camera.name,
        contentType: SnContentType.Camera,
      },
      operation: SnAuditOperation.VideoScreenShot,
      changeData: {
        screenShotVideoTime: this._player.timeStamp,
      },
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});

    this._toastrService.overlayContainer = this._player.toastContainer;
    this._player.isClipping = true;
    const video = this._player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const dialogRef = this._matDialog.open(SnShareImageDialogComponent, {
      data: {
        url: canvas.toDataURL(),
        camera: this.camera,
        playerRef: this._player,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => (this._player.isClipping = false));
  }

  public openClipControl(): void {
    this._toastrService.overlayContainer = this._player.toastContainer;
    if (this.dialogRef) {
      return;
    }
    this.dialogRef = this._dialog.open(VideoPlayerClipControl, {
      data: {
        timeStamp: this._player.reference.actualTime || this._player.timeStamp,
        camera: this.camera,
        fullscreen$: this._player.fullscreen$,
        playerRef: this._player,
        alwaysOn: this.alwaysOn,
      },
    });
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.dialogRef = null;
      });
  }

  public showClipControl(): boolean {
    if (this.camera.isFususCamera) {
      return (
        this.canClip &&
        !this._player.live &&
        ((this.camera.record || this.camera.playbackUrls) as boolean)
      );
    }
    if (!this.camera.isFususCamera) {
      return this.canClip && ((this.camera.record || this.camera.playbackUrls) as boolean);
    }
  }

  public enterStreetView(): void {
    this._streetViewDialogRef = this._dialog.open(MapStreetViewComponentComponent, {
      data: this.camera,
      backdropClass: 'not-see-through',
      panelClass: 'streetView-camera-dialog',
      minWidth: '40vw',
      maxWidth: '40vw',
      minHeight: '40vh',
      maxHeight: '40vh',
      height: '40vh',
      width: '40vw',
      position: { bottom: '250px', right: '20px' },
    });
  }

  public pauseRewind(): void {
    this._player.reference.pause();
    this._player.reference.stopRewinding();
    this._playbackService.rewindStop();
  }

  private _getVideoFramerate(now: DOMHighResTimeStamp, metadata: any): void {
    const video = this._player.elementRef.nativeElement.querySelector('video');
    const mediaTimeDiff = Math.abs(metadata.mediaTime - this.lastMediaTime);
    const frameNumDiff = Math.abs(metadata.presentedFrames - this.lastFrameNum);
    const diff = mediaTimeDiff / frameNumDiff;
    if (
      diff &&
      diff < 1 &&
      this.frameNotSeeked &&
      this.fpsRounder.length < 50 &&
      video.playbackRate === 1 &&
      document.hasFocus()
    ) {
      this.fpsRounder.push(diff);
      this.fps = Math.round(1 / this._getFpsAverage());
    }
    this.frameNotSeeked = true;
    this.lastMediaTime = metadata.mediaTime;
    this.lastFrameNum = metadata.presentedFrames;
    video.requestVideoFrameCallback(this._getVideoFramerate.bind(this));
  }

  private _getFpsAverage(): number {
    return this.fpsRounder.reduce((a, b) => a + b) / this.fpsRounder.length;
  }
}
